import * as React from "react";
import { useLocation, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Building2, ChevronsUpDown, Clock } from "lucide-react";
import { accounts } from "@/api/accounts";
import { useSession } from "@/auth/session";
import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { readRecentAccounts, rememberAccount, useAccount, useActingAccountId } from "./use-account";

/**
 * The account chip in the top bar and the ⌘K palette behind it. Search is
 * crossbar's prefix search; a swap keeps the same token and rewrites the
 * `/a/:accountId` segment so the current section stays open.
 */
export function AccountSwitcher({ className }: { className?: string }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const actingId = useActingAccountId();
  const own = useSession((s) => s.session);
  const acting = useAccount(actingId);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const search = useQuery({
    queryKey: ["account-search", q],
    queryFn: () => accounts.search(q),
    enabled: open && q.trim().length >= 2,
    staleTime: 30_000,
  });
  const recent = React.useMemo(() => (open ? readRecentAccounts() : []), [open]);

  const go = (id: string, name: string) => {
    rememberAccount({ id, name });
    setOpen(false);
    setQ("");
    const m = location.pathname.match(/^\/a\/[^/]+(\/.*)?$/);
    const rest = m?.[1] ?? "/pbx/users";
    navigate(`/a/${id}${rest}`);
  };

  const label = acting.data?.name ?? (actingId === own?.accountId ? own?.accountName : undefined) ?? "Choose account";

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className={cn("h-7 max-w-[260px] gap-2 border-border/80 bg-panel px-2 font-medium", className)}
        aria-label="Switch account"
      >
        <span className="size-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" />
        <span className="truncate">{label}</span>
        <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
        <kbd className="ml-1 hidden rounded-sm border px-1 font-mono text-2xs text-muted-foreground sm:inline">⌘K</kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen} title="Switch account" description="Search accounts by name" shouldFilter={false}>
        <CommandInput placeholder="Account name…" value={q} onValueChange={setQ} />
        <CommandList>
          {q.trim().length < 2 && recent.length > 0 && (
            <CommandGroup heading="Recent">
              {recent.map((r) => (
                <CommandItem key={r.id} value={r.id} onSelect={() => go(r.id, r.name)}>
                  <Clock className="size-3.5 text-muted-foreground" />
                  <span className="truncate">{r.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {own && q.trim().length < 2 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Home">
                <CommandItem value={own.accountId} onSelect={() => go(own.accountId, own.accountName ?? "Home account")}>
                  <Building2 className="size-3.5 text-muted-foreground" />
                  <span className="truncate">{own.accountName ?? own.accountId}</span>
                </CommandItem>
              </CommandGroup>
            </>
          )}
          {q.trim().length >= 2 && (
            <CommandGroup heading={search.isFetching ? "Searching…" : "Accounts"}>
              {search.data?.map((a) => (
                <CommandItem key={a.id} value={a.id} onSelect={() => go(a.id, a.name)}>
                  <Building2 className="size-3.5 text-muted-foreground" />
                  <span className="truncate">{a.name}</span>
                  {a.realm && <span className="ml-auto truncate font-mono text-2xs text-muted-foreground">{a.realm}</span>}
                  {typeof a.descendants_count === "number" && a.descendants_count > 0 && (
                    <span className="font-mono text-2xs text-muted-foreground">{a.descendants_count}</span>
                  )}
                </CommandItem>
              ))}
              {!search.isFetching && search.data?.length === 0 && <CommandEmpty>No account starts with “{q}”.</CommandEmpty>}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
